import { CdkDragDrop, DragDropModule } from '@angular/cdk/drag-drop';

import {
    afterNextRender,
    Component,
    computed,
    DestroyRef,
    effect,
    ElementRef,
    inject,
    Injector,
    signal,
    viewChild,
} from '@angular/core';
import { TranslatePipe } from '@ngx-translate/core';
import { RaButtonComponent, RaLoadingComponent } from '@shared/components';
import { RaIconComponent } from 'src/app/shared/components/ra-icon/ra-icon.component';
import {
    ChecklistStep,
    computeChecklistSteps,
} from 'src/app/shared/pipes/documents/checklist-completion.pipe';
import { RaEditTooltipDirective } from '../../directives/ra-edit-tooltip/ra-edit-tooltip.directive';
import { EditDataService } from '../../services/_edit-data.service';
import { EditSectionService } from '../../services/edit-section.service';
import { EditService } from '../../services/edit-service';
import { ChecklistBarComponent } from '../checklist-bar/checklist-bar.component';
import { SectionCardComponent } from './section-card/section-card.component';
import { SectionCardSkeletonComponent } from './section-card-skeleton/section-card-skeleton.component';

@Component({
    selector: 'sections-list',
    templateUrl: 'sections-list.component.html',
    styleUrl: 'sections-list.component.scss',
    host: {
        '[class.collapsed]': 'editDataService.sessionsCollapsed()',
        '[class.checklist-steps]': 'isChecklistSteps()',
    },
    imports: [
        DragDropModule,
        SectionCardComponent,
        SectionCardSkeletonComponent,
        ChecklistBarComponent,
        RaLoadingComponent,
        RaButtonComponent,
        RaEditTooltipDirective,
        TranslatePipe,
        RaIconComponent,
    ],
})
export class SectionsListComponent {
    public loadingCreateSession = signal(false);

    /** Quantidade de skeletons exibidos enquanto o documento carrega. */
    public sessionSkeletons = Array.from({ length: 5 });

    /** Quando a lista + botão não cabem na coluna, o botão fica fixo no rodapé; caso contrário segue o fluxo logo após a última seção. */
    public sessionsListOverflow = signal(false);

    private readonly _containerSessions = viewChild<ElementRef<HTMLElement>>('containerSessions');
    private readonly _sessionsScroll = viewChild<ElementRef<HTMLElement>>('sessionsScroll');

    private readonly _injector = inject(Injector);
    private _resizeObserver?: ResizeObserver;

    // * [Injects]
    public editService = inject(EditService);
    public editDataService = inject(EditDataService);
    private _editSessionService = inject(EditSectionService);
    private _destroyRef = inject(DestroyRef);

    // * [Checklist: trilha de etapas]

    /** Modo checklist com a lista aberta: as seções viram a trilha de etapas. */
    public isChecklistSteps = computed(
        () =>
            (this.editDataService.document()?.config?.checklistMode ?? false) &&
            !this.editDataService.sessionsCollapsed()
    );

    /** Etapa de cada seção (por id), para o card desenhar o nó e o trecho da trilha. */
    public checklistSteps = computed(() => {
        if (!this.isChecklistSteps()) return new Map<string, ChecklistStep>();

        const { steps } = computeChecklistSteps(this.editDataService.document()?.sessions);
        return new Map(steps.map(step => [step.sessionId, step]));
    });

    constructor() {
        afterNextRender(() => {
            this.updateSessionsListOverflow();
            this._resizeObserver = new ResizeObserver(() => this.scheduleOverflowUpdate());
            const container = this._containerSessions()?.nativeElement;
            const scroll = this._sessionsScroll()?.nativeElement;
            if (container) {
                this._resizeObserver.observe(container);
            }
            if (scroll) {
                this._resizeObserver.observe(scroll);
            }
        });

        this._destroyRef.onDestroy(() => this._resizeObserver?.disconnect());

        effect(() => {
            this.editDataService.document()?.sessions?.length;
            this.loadingCreateSession();
            this.scheduleOverflowUpdate();
        });
    }

    private scheduleOverflowUpdate(): void {
        queueMicrotask(() => {
            requestAnimationFrame(() => this.updateSessionsListOverflow());
        });
    }

    /** Rola a lista até o fim para mostrar a seção recém-criada (útil quando o botão add está fixo no rodapé). */
    private scrollSessionsListToEnd(): void {
        const scroll = this._sessionsScroll()?.nativeElement;
        if (!scroll || scroll.scrollHeight <= scroll.clientHeight) {
            return;
        }

        scroll.scrollTo({ top: scroll.scrollHeight, behavior: 'smooth' });
    }

    private updateSessionsListOverflow(): void {
        const container = this._containerSessions()?.nativeElement;
        const scroll = this._sessionsScroll()?.nativeElement;
        if (!container || !scroll) {
            return;
        }

        const overflow = scroll.scrollHeight > container.clientHeight + 1;
        this.sessionsListOverflow.set(overflow);
    }

    /**
     * Cria uma seção
     */
    public createSession(): void {
        this.loadingCreateSession.set(true);

        // IDs das seções antes da criação, para identificar a recém-criada
        const previousIds = new Set(
            (this.editDataService.document()?.sessions ?? []).map(section => section._id)
        );

        this._editSessionService.createSession(this.editDataService.getDocumentId()).subscribe({
            next: document => {
                this.loadingCreateSession.set(false);

                // Navega para a nova seção (a que não existia antes; fallback: última)
                const sessions = document?.sessions ?? [];
                const newSession =
                    sessions.find(section => !previousIds.has(section._id)) ??
                    sessions[sessions.length - 1];
                if (newSession) {
                    this.editDataService.setCurrentSession(newSession);
                }

                afterNextRender(
                    () => {
                        requestAnimationFrame(() => this.scrollSessionsListToEnd());
                    },
                    { injector: this._injector }
                );
            },
            error: error => {
                console.error(error);
                this.loadingCreateSession.set(false);
            },
        });
    }

    /**
     * Handler para quando uma seção é arrastada e solta
     * @param event Evento de drag and drop
     */
    public drop(event: CdkDragDrop<any[]>): void {
        this.editDataService.reorderSessions(event.previousIndex, event.currentIndex);
        this.scheduleOverflowUpdate();
    }
}
